import { formatInTimeZone } from 'date-fns-tz';
import { tv } from 'tailwind-variants';

import { Text } from '@/components/text';
import { DATE_MONTH_FORMAT, MODE_WATER, TIME_FORMAT } from '@/lib/constants';
import { RouteLocationInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';

import { routeSegmentPanelStyles } from '../shared/styles';

export interface TimelineBarProps {
  mode: SegmentModeType;
  origin: RouteLocationInterface;
  destination: RouteLocationInterface;
  shouldShowTimezones: boolean;
  bars: Date[];
  shouldShowRates?: boolean;
  className?: string;
}

const TimelineBar = ({
  mode,
  origin,
  destination,
  shouldShowTimezones,
  bars,
  className,
  shouldShowRates = false,
}: TimelineBarProps) => {
  const isOceanSchedule = mode === MODE_WATER;

  const originTimezone = origin.timezoneName;
  const destinationTimezone = destination.timezoneName;

  // For tracking day changes
  let previousOrigDay = '';
  let previousDestDay = '';

  const { bold } = routeSegmentPanelStyles();

  const { base, bar, barLabelLow, barLabelTime, barLabelDate, legendRow, legend, rowTitle } = timelineBarStyles({
    isOceanSchedule,
    shouldShowTimezones,
    withPrice: shouldShowRates,
  });

  return (
    <div className={base({ className })}>
      <div className="flex h-full pl-8">
        <div className={legendRow()}>
          <div />
          <div className={legend()}>Departure</div>
          <div className={legend()}>Arrival</div>
          <div className={legend()}>Duration</div>
          <div className={legend()}>Est. Emissions</div>
          <div className="relative z-6 mr-8 grid h-full auto-cols-fr grid-flow-col [&>:last-child]:absolute [&>:last-child]:left-full">
            {bars.map((dateTime, index) => {
              // For determining if we show next day Date
              const origDay = formatInTimeZone(dateTime, originTimezone, 'd');
              const destDay = formatInTimeZone(dateTime, destinationTimezone, 'd');
              const isDiffOrigDay = origDay !== previousOrigDay;
              const isDiffDestDay = destDay !== previousDestDay;

              // Update store on loop
              previousOrigDay = origDay;
              previousDestDay = destDay;

              const isLastRow = index + 1 === bars.length;

              return (
                <div className={bar()} key={dateTime.toString()}>
                  <div className="relative whitespace-nowrap text-sm text-text-primary">
                    {isOceanSchedule ? (
                      <div className={barLabelLow({ isLastRow })}>
                        <span className={barLabelTime()}>
                          {formatInTimeZone(dateTime, originTimezone, DATE_MONTH_FORMAT)}
                        </span>
                      </div>
                    ) : (
                      <div className={barLabelLow({ isLastRow })}>
                        <span className={barLabelTime()}>
                          {formatInTimeZone(dateTime, originTimezone, TIME_FORMAT)}
                        </span>
                        {isDiffOrigDay && !isLastRow && (
                          <span className={barLabelDate()}>
                            {formatInTimeZone(dateTime, originTimezone, DATE_MONTH_FORMAT)}
                          </span>
                        )}
                      </div>
                    )}
                    {shouldShowTimezones && (
                      <div className={barLabelLow({ isLastRow })}>
                        <span className={barLabelTime()}>
                          {formatInTimeZone(dateTime, destinationTimezone, TIME_FORMAT)}
                        </span>
                        {isDiffDestDay && !isLastRow && (
                          <span className={barLabelDate()}>
                            {formatInTimeZone(dateTime, destinationTimezone, DATE_MONTH_FORMAT)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            {shouldShowTimezones && (
              <>
                <Text className={rowTitle()}>
                  Time at <b className={bold()}>{getPreferredIdentifier(origin.identifiers)?.value}</b>
                </Text>
                <Text className={rowTitle()}>
                  Time at <b className={bold()}>{getPreferredIdentifier(destination.identifiers)?.value}</b>
                </Text>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const timelineBarStyles = tv({
  slots: {
    base: 'absolute bottom-0 left-2 right-2 top-1.5 z-2 hidden md:block',
    bar: 'h-full border-l border-dashed border-l-grey-300',
    legendRow: 'grid h-full w-full grid-cols-[--timeline-spacing]',
    legend:
      'legend mt-0 flex h-6 items-center px-4 text-sm text-text-secondary [&+_.legend]:border-l [&+_.legend]:border-l-grey-200',
    rowTitle: 'm-0 flex h-6 items-center justify-center p-0 text-sm [&_*]:ml-1',
    barLabelLow:
      'bar-label relative flex h-[1.5rem] items-center px-2 [&+_.bar-label]:border-t [&+_.bar-label]:border-t-grey-200',
    barLabelTime: 'font-medium',
    barLabelDate: 'ml-1 text-text-secondary',
  },
  variants: {
    isOceanSchedule: {
      true: {
        legend: 'mt-0',
      },
    },
    shouldShowTimezones: {
      true: {
        legend: 'mt-5',
      },
    },
    isLastRow: {
      true: {
        barLabelLow: 'bg-transparent',
      },
    },
    withPrice: {
      true: {
        legendRow: 'grid-cols-[--timeline-spacing-with-price]',
      },
    },
  },
});

export { TimelineBar };
